import type { RememberedSearch } from '../utils/remembered-search-store';
import { resultsQuery, type ResultsCriteria } from '../utils/results-query';

/** Looks a translation up by key, as `i18n.t` does. */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * Apply a Remembered Search to the search component's markup.
 *
 * Writes each remembered criterion into the control's existing `selected`
 * data attribute(s) — the inputs the component's defaults path reads on
 * construction — and sets each button's text and accessible name from the
 * stored display names, so a remembered "any" reads as "any" even where the
 * page authored a value. It applies nothing itself beyond that: the defaults
 * path remains the only place selections reach the modals.
 *
 * Deliberately free of the component and its modals (it needs only the markup
 * and a translator) so it can also run before the component loads.
 */
export const applyRememberedSearch = (
  container: ParentNode,
  search: RememberedSearch,
  t: Translate
): void => {
  // A control the page pinned (marked `data-pinned` at build time) keeps what
  // the page authored; the marker is the only thing consulted.
  const control = (selector: string) =>
    container.querySelector<HTMLElement>(`${selector}:not([data-pinned])`);
  const destination = control('button[data-search-destination]');
  const airport = control('button[data-search-airport]');
  const duration = control('button[data-search-duration]');
  const date = control('button[data-search-date]');
  const rooms = control('button[data-search-room]');

  // The page's own values are kept aside before memory replaces them, so a
  // remembered choice the page no longer offers can fall back to them, and
  // the whole Remembered Search can be withdrawn. Dates are never authored
  // (stale ones fall back to "any"); theirs are kept only for withdrawing.
  for (const control of [destination, airport, duration, date, rooms]) {
    if (control) keepAuthored(control);
  }

  if (destination) {
    const chosen = search.destination === 'any' ? [] : search.destination;
    destination.dataset.selectedDestinations = chosen
      .filter(item => !item.isHotel)
      .map(item => item.id)
      .join(',');
    destination.dataset.selectedAccommodations = JSON.stringify(
      chosen.filter(item => item.isHotel).map(({ id, name }) => ({ id, name }))
    );
    label(
      destination,
      summarise(chosen.map(item => item.name), t('search.anyDestination')),
      t('search.selectDestinationAria').replace(
        '{destination}',
        listOrAny(chosen.map(item => item.name), t('search.anyDestination'))
      )
    );
  }

  if (airport) {
    const chosen = search.departure === 'any' ? [] : search.departure;
    airport.dataset.selectedAirports = chosen.map(item => item.code).join(',');
    label(
      airport,
      summarise(chosen.map(item => item.name), t('search.anyAirport')),
      t('search.selectAirportAria').replace(
        '{airport}',
        listOrAny(chosen.map(item => item.name), t('search.anyAirport'))
      )
    );
  }

  if (duration) {
    const chosen = search.duration === 'any' ? null : search.duration;
    duration.dataset.selectedDuration = chosen ? String(chosen.value) : '';
    const name = chosen?.name ?? t('search.anyDuration');
    label(duration, name, t('search.selectDurationAria').replace('{duration}', name));
  }

  if (date) applyDates(date, search.dates, t);

  if (rooms && search.rooms.length > 0) {
    rooms.dataset.selectedRooms = JSON.stringify(
      search.rooms.map(room => ({
        travellersAdults: room.adults,
        travellersChildrenAge: room.childAges,
      }))
    );
    const adults = search.rooms.reduce((sum, room) => sum + room.adults, 0);
    const children = search.rooms.reduce((sum, room) => sum + room.childAges.length, 0);
    let text = `${search.rooms.length} ${t(search.rooms.length > 1 ? 'search.rooms' : 'search.room')}`;
    if (adults > 0) text += `: ${adults} ${t(adults > 1 ? 'search.adults' : 'search.adult')}`;
    if (children > 0) {
      text += `, ${children} ${t(children > 1 ? 'search.children' : 'search.child')}`;
    }
    // `rooms: 1` matches the component's own accessible name for the button.
    label(rooms, text, t('search.selectRoomAria', { rooms: 1, adults, children }));
  }

  // The Search link goes where the inputs now say — pinned ones included —
  // so a click before the component loads searches what the visitor sees.
  const link = container.querySelector<HTMLElement>('a[data-search-button]');
  if (link) {
    link.dataset.authoredHref ??= link.getAttribute('href') ?? '';
    const path = link.dataset.authoredHref.split('?')[0];
    link.setAttribute('href', `${path}?${resultsQuery(criteriaOf(container))}`);
  }
};

/**
 * Undo `applyRememberedSearch`: put every authored value, label and the
 * Search link back as the page rendered them. The component does this when
 * its own reading of storage offers nothing for the early script to have
 * applied.
 */
export const withdrawRememberedSearch = (container: ParentNode): void => {
  container
    .querySelectorAll<HTMLElement>('button[data-modal], a[data-search-button]')
    .forEach(control => {
      const inputs = control.dataset;
      const authored = Object.keys(inputs).filter(key => key.startsWith('authored'));
      if (authored.length === 0) return;
      if (control.matches('button[data-search-date]')) {
        // Dates are never authored: everything about them goes.
        inputs.flexibility = inputs.wholeMonth = inputs.minDate = inputs.maxDate = '';
      }
      // An input the page never rendered (a remembered hotel) goes entirely.
      for (const key of Object.keys(inputs)) {
        const rest = key.slice('selected'.length);
        if (key.startsWith('selected') && inputs[`authored${rest}`] === undefined) {
          delete inputs[key];
        }
      }
      for (const key of authored) {
        const value = inputs[key] ?? '';
        if (key === 'authoredLabel') {
          const span = control.querySelector('span');
          if (span) span.textContent = value;
        } else if (key === 'authoredAriaLabel') {
          control.setAttribute('aria-label', value);
        } else if (key === 'authoredHref') {
          control.setAttribute('href', value);
        } else {
          inputs[`selected${key.slice('authored'.length)}`] = value;
        }
        delete inputs[key];
      }
    });
};

/** What the search component's inputs currently ask the results page for. */
const criteriaOf = (container: ParentNode): ResultsCriteria => {
  const inputs = (selector: string) =>
    container.querySelector<HTMLElement>(selector)?.dataset ?? {};
  const destination = inputs('button[data-search-destination]');
  const date = inputs('button[data-search-date]');
  const codes = list(inputs('button[data-search-airport]').selectedAirports);
  const rooms = parse(inputs('button[data-search-room]').selectedRooms);
  const hotels = parse(destination.selectedAccommodations);
  const options = parse(
    container.querySelector<HTMLElement>('.search-component')?.dataset.searchOptions
  );
  return {
    destinationIds: list(destination.selectedDestinations)
      .map(id => parseInt(id))
      .filter(id => !isNaN(id)),
    hotelIds: (Array.isArray(hotels) ? hotels : [])
      .map(hotel => hotel?.id)
      .filter((id): id is number => typeof id === 'number'),
    // Airport group ids are numeric; airport codes never are.
    airportGroupIds: codes.filter(code => /^\d+$/.test(code)),
    airportCodes: codes.filter(code => !/^\d+$/.test(code)),
    duration: inputs('button[data-search-duration]').selectedDuration || null,
    dates:
      date.selectedDate && date.minDate && date.maxDate
        ? { min: date.minDate, max: date.maxDate }
        : null,
    // As the defaults path reads them: one room, or several.
    rooms: (Array.isArray(rooms) ? rooms : rooms ? [rooms] : []).map(room => ({
      adults: room?.travellersAdults,
      childAges: room?.travellersChildrenAge || [],
    })),
    options: Array.isArray(options) ? options : [],
  };
};

const list = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);

/** A JSON input, or undefined when it is absent or unreadable. */
const parse = (value: string | undefined): any => {
  try {
    return value ? JSON.parse(value) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Copy each `data-selected-*` value to a parallel `data-authored-*` one. Only
 * the first copy counts: applying again (e.g. once before paint and once on
 * load) must not mistake a remembered value for the authored one.
 */
const keepAuthored = (control: HTMLElement): void => {
  const entries = Object.entries(control.dataset);
  if (entries.some(([key]) => key.startsWith('authored'))) return;
  for (const [key, value] of entries) {
    if (key.startsWith('selected') && value !== undefined) {
      control.dataset[`authored${key.slice('selected'.length)}`] = value;
    }
  }
};

/**
 * The date control's inputs: the date chosen (a whole month's first day), its
 * flexibility, whether it is a whole month, and the range they search — which
 * the defaults path re-derives, and checks against the departure window.
 */
const applyDates = (
  button: HTMLElement,
  dates: RememberedSearch['dates'],
  t: Translate
): void => {
  const inputs = button.dataset;
  if (dates === 'any') {
    inputs.selectedDate = inputs.flexibility = inputs.wholeMonth = '';
    inputs.minDate = inputs.maxDate = '';
    label(button, t('search.anyDate'), t('search.selectDateAria', { type: 'empty' }));
    return;
  }

  if (dates.kind === 'month') {
    const [year, month] = dates.month.split('-').map(Number);
    inputs.selectedDate = inputs.minDate = isoDate(new Date(year, month - 1, 1));
    inputs.maxDate = isoDate(new Date(year, month, 0));
    inputs.flexibility = '';
    inputs.wholeMonth = 'true';
    label(
      button,
      dates.label,
      t('search.selectDateAria', { type: 'month', date: dates.label, flexibility: 0 })
    );
    return;
  }

  const [year, month, day] = dates.date.split('-').map(Number);
  const flexibility = dates.flexibility ?? 0;
  inputs.selectedDate = dates.date;
  inputs.flexibility = dates.flexibility === null ? '' : String(dates.flexibility);
  inputs.wholeMonth = '';
  inputs.minDate = isoDate(new Date(year, month - 1, day - flexibility));
  inputs.maxDate = isoDate(new Date(year, month - 1, day + flexibility));
  label(
    button,
    dates.flexibility === null
      ? dates.label
      : `${dates.label} ${t('modal.date.flexibleDays', { days: dates.flexibility })}`,
    t('search.selectDateAria', {
      type: dates.flexibility === null ? 'other' : 'flexible',
      date: dates.label,
      flexibility,
    })
  );
};

/** `YYYY-MM-DD`, in local time, as the date control writes dates. */
const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** "Spain", "Spain + 2", or the "any" wording, as the buttons show them. */
const summarise = (names: string[], any: string): string => {
  if (names.length > 1) return `${names[0]} + ${names.length - 1}`;
  return names[0] ?? any;
};

const listOrAny = (names: string[], any: string): string =>
  names.length > 0 ? names.join(', ') : any;

/** Relabel `button`, keeping the page's own label aside (once) for withdrawing. */
const label = (button: HTMLElement, text: string, accessibleName: string): void => {
  const span = button.querySelector('span');
  button.dataset.authoredLabel ??= span?.textContent ?? '';
  button.dataset.authoredAriaLabel ??= button.getAttribute('aria-label') ?? '';
  if (span) span.textContent = text;
  button.setAttribute('aria-label', accessibleName);
};
